import React, { useMemo } from "react";
import { Icon } from "semantic-ui-react";

import styles from "./ProfitChart.module.scss";

const chart = {
  width: 700,
  height: 230,
  paddingTop: 22,
  paddingRight: 20,
  paddingBottom: 34,
  paddingLeft: 58,
};

function formatCurrency(value, options = {}) {
  return `Q ${(Number(value) || 0).toLocaleString("es-GT", {
    maximumFractionDigits: 0,
    ...options,
  })}`;
}

// "Invertido" (costo) e "Ingresos" ya se ven como KPI arriba -- cruzarlos
// en la misma línea que "Ganancia" no aporta nada (la ganancia YA es
// ingresos menos costo), así que la gráfica solo traza esa, con costo/
// ingresos como referencia numérica.
export function ProfitChart({ data = [], isLoading = false }) {
  const chartData = useMemo(() => {
    const profitValues = data.map((item) => Number(item.profit) || 0);

    const maxValue = Math.max(...profitValues, 0);
    const minValue = Math.min(...profitValues, 0);
    const range = maxValue - minValue || 1;

    const graphWidth = chart.width - chart.paddingLeft - chart.paddingRight;
    const graphHeight = chart.height - chart.paddingTop - chart.paddingBottom;
    const slot = graphWidth / Math.max(data.length - 1, 1);

    function getX(index) {
      return data.length === 1
        ? chart.paddingLeft + graphWidth / 2
        : chart.paddingLeft + slot * index;
    }

    function getY(value) {
      return (
        chart.paddingTop +
        graphHeight -
        ((value - minValue) / range) * graphHeight
      );
    }

    const points = data.map((item, index) => {
      const value = Number(item.profit) || 0;

      return {
        key: item.key,
        label: item.label,
        x: getX(index),
        y: getY(value),
        value,
        isNegative: value < 0,
      };
    });

    const linePath = points
      .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
      .join(" ");

    const baselineY = getY(0);

    const areaPath =
      points.length > 0
        ? `${linePath} L ${points[points.length - 1].x} ${baselineY} L ${points[0].x} ${baselineY} Z`
        : "";

    const yLabels = Array.from({ length: 4 }, (_, index) => {
      const value = maxValue - ((maxValue - minValue) / 3) * index;
      const y = chart.paddingTop + (graphHeight / 3) * index;

      return { value, y };
    });

    return {
      points,
      linePath,
      areaPath,
      baselineY,
      yLabels,
      showBaseline: minValue < 0,
    };
  }, [data]);

  const totalProfit = data.reduce(
    (total, item) => total + (Number(item.profit) || 0),
    0
  );

  const totalRevenue = data.reduce(
    (total, item) => total + (Number(item.revenue) || 0),
    0
  );

  const totalCost = data.reduce(
    (total, item) => total + (Number(item.cost) || 0),
    0
  );

  const hasAnySales = data.some((item) => Number(item.unitsSold) > 0);

  return (
    <section className={styles.ProfitChart}>
      <header className={styles.ChartHeader}>
        <div>
          <h2>Ganancias</h2>
          <p>Ganancia por mes, según las salidas de inventario.</p>
        </div>
      </header>

      <div className={styles.ChartSummary}>
        <div>
          <span className={`${styles.LegendDot} ${styles.ProfitLegend}`} />
          <span className={styles.SummaryLabel}>Ganancia del período</span>
          <strong>{formatCurrency(totalProfit)}</strong>
        </div>

        <div>
          <span className={styles.SummaryLabel}>Ingresos</span>
          <strong>{formatCurrency(totalRevenue)}</strong>
        </div>

        <div>
          <span className={styles.SummaryLabel}>Invertido</span>
          <strong>{formatCurrency(totalCost)}</strong>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.EmptyState}>
          <span>Cargando...</span>
        </div>
      ) : !hasAnySales ? (
        <div className={styles.EmptyState}>
          <Icon name="chart line" />
          <span>
            Todavía no hay salidas con precio de venta registrado.
          </span>
        </div>
      ) : (
        <div className={styles.ChartWrapper}>
          <svg
            viewBox={`0 0 ${chart.width} ${chart.height}`}
            className={styles.ChartSvg}
            role="img"
            aria-label="Gráfica de ganancia mensual"
          >
            <defs>
              <linearGradient id="profitAreaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8f2350" stopOpacity="0.16" />
                <stop offset="100%" stopColor="#8f2350" stopOpacity="0" />
              </linearGradient>
            </defs>

            {chartData.yLabels.map((item) => (
              <g key={item.y}>
                <line
                  x1={chart.paddingLeft}
                  x2={chart.width - chart.paddingRight}
                  y1={item.y}
                  y2={item.y}
                  className={styles.GridLine}
                />

                <text
                  x={chart.paddingLeft - 9}
                  y={item.y + 4}
                  textAnchor="end"
                  className={styles.YAxisLabel}
                >
                  {formatCurrency(item.value)}
                </text>
              </g>
            ))}

            {chartData.showBaseline && (
              <line
                x1={chart.paddingLeft}
                x2={chart.width - chart.paddingRight}
                y1={chartData.baselineY}
                y2={chartData.baselineY}
                className={styles.BaselineLine}
              />
            )}

            <path d={chartData.areaPath} className={styles.AreaFill} />
            <path d={chartData.linePath} className={styles.ProfitLine} />

            {chartData.points.map((point) => (
              <circle
                key={point.key}
                cx={point.x}
                cy={point.y}
                r={3.5}
                className={
                  point.isNegative ? styles.NegativePoint : styles.ProfitPoint
                }
              >
                <title>{`${point.label}: ${formatCurrency(point.value)}`}</title>
              </circle>
            ))}

            {chartData.points.map((point) => (
              <text
                key={point.key}
                x={point.x}
                y={chart.height - 12}
                textAnchor="middle"
                className={styles.XAxisLabel}
              >
                {point.label}
              </text>
            ))}
          </svg>
        </div>
      )}
    </section>
  );
}
